import * as React from 'react'
import { Select } from '../shared/select.component'
import { CountriesData } from '../../services/models/countries.service'
import { I18nService } from '../../services/utils/i18n.service'
import { usersI18nCivility } from '../../services/models/users.service'
import LoadingComponent from './loading.component'
import { usePromise } from '../../hooks/use-promise'
import { errorsSetFlash } from '../../services/utils/errors.service'
import { Input } from '../plugins/casting/casting-styled.component'
import { addressesNew } from '../../services/models/addresses.service'

type Props<T extends string> = {
  name: string
  prefix: T
  disabled?: boolean
  address: { [K in 'street' | 'zipcode' | 'city' | 'state' | 'country' as `${T}${K}`]: string }
  countryDisabled?: boolean
  countries?: CountriesData
  children?: React.ReactNode
  onChange (address: Props<T>['address']): void
} & (
  {
    hasIdentity: boolean
    isCorporate: boolean
    address: { [K in 'first_name' | 'last_name' | 'corporate_name' | 'civility' as `${T}${K}`]: K extends 'civility' ? `${T}${'mister' | 'madam'}` : string }
  } | {
    hasIdentity?: never
    isCorporate?: never
  }
) & (
  {
    hasAdditional: boolean
    address: { [K in 'additional' as `${T}${K}`]: string }
  } | {
    hasAdditional?: never
  }
)

const i18nScoped = new I18nService(require('./postal-address-input.component.yml'))

export default function PostalAddressInput<T extends string> ({ hasAdditional, hasIdentity, isCorporate, countryDisabled, address, disabled, prefix, name, onChange, children, ...otherProps }: Props<T>) {
  const countries = usePromise<CountriesData>(async () => {
    if (otherProps.countries) return otherProps.countries
    return addressesNew().then(_ => _.countries)
  }, [otherProps.countries], { setErrors: errorsSetFlash, defaultState: [] })
  
  const setAddrState = React.useCallback((body: typeof address) => {
    onChange({ ...address, ...body })
  }, [address, onChange])

  if (!countries.length) return <LoadingComponent align="center" />

  const country = countries.find(_ => _.id === (address as any)[`${prefix}country`])
  const civilityChoices = (['mister', 'madam'] as const).map(value => ({ value: `${prefix}${value}`, label: usersI18nCivility(value === 'mister' ? 'mr' : 'ms', 'short') }))
  const isStateRequired = country?.subdivisions.length > 0

  return <>
    { hasIdentity && isCorporate && <Input
      type="text"
      id={ name + '_corporate_name' }
      placeholder={ i18nScoped.t('corporate_name') }
      disabled={ disabled }
      value={ (address as any)[`${prefix}corporate_name`] ?? '' }
      onChange={ event => setAddrState({ [`${prefix}corporate_name`]: event.target.value } as any) } /> }

    { hasIdentity && !isCorporate && <div style={{ display: 'flex' }}>
      <div style={{ flexGrow: 1, marginRight: '0.625em' }}>
        <Select
          id={ name + '_civility' }
          value={ civilityChoices.find(_ => _.value === (address as any)[`${prefix}civility`]) }
          options={ civilityChoices }
          onChange={ val => setAddrState({ [`${prefix}civility`]: val.value } as any) }
          styles={{ container: base => ({ ...base, width: '6em' }) }}
          isDisabled={ disabled }
          isSearchable={ false } />
      </div>

      <div style={{ flexGrow: 2, marginRight: '0.625em' }}>
        <Input
          type="text"
          id={ name + '_first_name' }
          disabled={ disabled }
          value={ (address as any)[`${prefix}first_name`] ?? '' }
          onChange={ event => setAddrState({ [`${prefix}first_name`]: event.target.value } as any) }
          placeholder={ i18nScoped.t('first_name') } />
      </div>

      <div style={{ flexGrow: 2 }}>
        <Input
          type="text"
          id={ name + '_last_name' }
          disabled={ disabled }
          value={ (address as any)[`${prefix}last_name`] ?? '' }
          onChange={ event => setAddrState({ [`${prefix}last_name`]: event.target.value } as any) }
          placeholder={ i18nScoped.t('last_name') } />
      </div>
    </div> }

    <Input
      type="text"
      id={ name + '_street' }
      placeholder={ i18nScoped.t('street_and_number') }
      disabled={ disabled }
      value={ (address as any)[`${prefix}street`] ?? '' }
      onChange={ evt => setAddrState({ [`${prefix}street`]: evt.target.value } as any) } />

    <div style={{ display: 'flex', marginTop: '0.625em' }}>
      <Input
        type="text"
        id={ name + '_zipcode' }
        style={{ width: '10em', marginRight: '0.625em' }}
        placeholder={ i18nScoped.t('zipcode') }
        disabled={ disabled }
        value={ (address as any)[`${prefix}zipcode`] ?? '' }
        onChange={ evt => setAddrState({ [`${prefix}zipcode`]: evt.target.value } as any) } />

      <Input
        type="text"
        id={ name + '_city' }
        style={{ flexGrow: 1 }}
        placeholder={ i18nScoped.t('city') }
        disabled={ disabled }
        value={ (address as any)[`${prefix}city`] ?? '' }
        onChange={ evt => setAddrState({ [`${prefix}city`]: evt.target.value } as any) } />
    </div>

    <div style={{ display: 'flex' }}>
      <div style={{ flexGrow: 1 }}>
        <Select
          id={ name + '_country' }
          placeholder={ i18nScoped.t('country') }
          value={ country }
          options={ countries }
          getOptionLabel={ val => val.text }
          getOptionValue={ val => val.id }
          onChange={ val => setAddrState({ [`${prefix}country`]: val.id, [`${prefix}state`]: null } as any) }
          isDisabled={ disabled || countryDisabled } />
      </div>

      { isStateRequired && <div className="u-mls" style={{ flexGrow: 1 }}>
        <Select
          id={ name + '_state' }
          placeholder={ i18nScoped.t('state') }
          value={ country.subdivisions.find(_ => _.id === (address as any)[`${prefix}state`]) }
          options={ country.subdivisions }
          getOptionLabel={ val => val.text }
          getOptionValue={ val => val.id }
          onChange={ val => setAddrState({ [`${prefix}state`]: val.id } as any) }
          isDisabled={ disabled } />
      </div> }
    </div>

    { hasAdditional && <Input
      type="text"
      style={{ marginTop: '0.625em' }}
      placeholder={ i18nScoped.t('additional') }
      disabled={ disabled }
      value={ (address as any)[`${prefix}additional`] || '' }
      onChange={ evt => setAddrState({ [`${prefix}additional`]: evt.target.value } as any) } /> }

    { children }
  </>
}
